/**
 * Sworn Translator Q&A — Wave 3 (embassy-list workflow, acceptance, pitfalls).
 *
 * ACCURACY POLICY (ตรวจทาน ส.ค. 2026)
 * - นักแปลสาบานตน = นักแปลที่อยู่ในบัญชีของสถานเอกอัครราชทูตนั้น ๆ ในไทย
 *   บัญชีรายชื่อและเงื่อนไขเปลี่ยนแปลงได้ตามประกาศของแต่ละสถานทูต
 * - นิติกรณ์คำแปล/เอกสารทำที่กองสัญชาติและนิติกรณ์ กรมการกงสุล
 *   ค่าธรรมเนียม 200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ไทยยังไม่ออกตรา Apostille จนอนุสัญญามีผล 28 ก.พ. 2027 (src/data/legal-facts.ts)
 * - ห้ามระบุค่าบริการของสำนักงาน · ไม่รับประกันผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */
import type { SwornQA } from "./questions-500";

const NOTE =
  "ข้อมูล ณ ส.ค. 2026 — บัญชีนักแปลและเงื่อนไขของแต่ละสถานทูตเปลี่ยนแปลงได้ โปรดยืนยันกับสถานทูตปลายทางก่อนยื่นทุกครั้ง";
const CTA = "สอบถามรายละเอียดและค่าบริการทางโทรศัพท์ LINE หรืออีเมล";
const FEE =
  "ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลคือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน (consular.mfa.go.th) ค่าธรรมเนียมราชการแยกจากค่าบริการของสำนักงานเสมอ";

export const SWORN_QA_WAVE3: SwornQA[] = [
  // ── นิยาม / การยอมรับ ────────────────────────────────────────────
  {
    slug: "sworn-ทำไมสถานทูตต้องใช้นักแปลในบัญชี",
    q: "ทำไมบางสถานทูตยอมรับเฉพาะนักแปลในบัญชีของตัวเอง?",
    aShort:
      "เพราะสถานทูตต้องตรวจสอบตัวผู้แปลและลายมือชื่อได้ นักแปลในบัญชีจึงถูกบันทึกตัวอย่างลายมือชื่อและตราไว้ล่วงหน้า ทำให้ตรวจสอบความถูกต้องได้ทันที",
    aLong: `ระบบบัญชีนักแปลเป็นกลไกควบคุมคุณภาพและป้องกันเอกสารปลอม เมื่อคำแปลมาจากนักแปลที่สถานทูตรับรองไว้ เจ้าหน้าที่จะเทียบลายมือชื่อกับตัวอย่างในระบบได้ กรณีใช้นักแปลนอกบัญชี เอกสารมักถูกขอให้แปลใหม่ทั้งฉบับ ซึ่งเสียเวลามากกว่าการเริ่มต้นให้ถูกทางตั้งแต่แรก ${NOTE} ${CTA}`,
    synonyms: [
      "นักแปลในบัญชีสถานทูต",
      "embassy list of sworn translators thailand",
      "ทำไมต้องใช้นักแปลสาบานตน",
      "sworn translator list embassy",
    ],
    cluster: "acceptance",
  },
  {
    slug: "sworn-ใช้นักแปลนอกบัญชีได้ไหม",
    q: "ใช้นักแปลนอกบัญชีสถานทูตได้ไหม?",
    aShort:
      "ขึ้นกับสถานทูต บางแห่งยอมรับคำแปลทั่วไปที่ผ่านนิติกรณ์กรมการกงสุล แต่สถานทูตกลุ่มยุโรปหลายแห่งกำหนดนักแปลในบัญชีอย่างเคร่งครัด",
    aLong: `วิธีตรวจที่เร็วที่สุดคืออ่านเช็กลิสต์ของสถานทูตว่าใช้คำว่า sworn หรือ assermenté หรือ beglaubigt หรือระบุว่า from the embassy list หากมีคำเหล่านี้ ให้ถือว่าต้องใช้นักแปลในบัญชี การเดาแล้วยื่นผิดชั้นทำให้ต้องเริ่มใหม่ตั้งแต่ขั้นแปล ${NOTE} ${CTA}`,
    synonyms: [
      "นักแปลนอกบัญชี สถานทูต",
      "non listed translator accepted",
      "แปลเองได้ไหม สถานทูต",
    ],
    cluster: "acceptance",
  },
  {
    slug: "sworn-ลำดับขั้นตอนที่ถูกต้อง",
    q: "ลำดับขั้นตอนที่ถูกต้องของงานแปลสาบานตนคืออะไร?",
    aShort:
      "โดยทั่วไปคือ ขอเอกสารฉบับจากผู้ออก แล้วแปลกับนักแปลในบัญชี แล้วยื่นนิติกรณ์ตามที่ปลายทางกำหนด และปิดท้ายด้วยตราสถานทูตเมื่อสถานทูตนั้นกำหนด",
    aLong: `ลำดับสำคัญมาก เพราะการทำสลับชั้นทำให้ชั้นถัดไปไม่รับ เช่น การประทับตราสถานทูตก่อนที่คำแปลจะครบถ้วน หรือการยื่นนิติกรณ์ก่อนที่เอกสารต้นทางจะได้รับรองจากผู้ออก บางปลายทางไม่ต้องการชั้นนิติกรณ์เลย จึงต้องอ่านเช็กลิสต์ก่อนเริ่ม ${FEE} ${NOTE} ${CTA}`,
    synonyms: [
      "ขั้นตอนแปลสาบานตน",
      "sworn translation process order",
      "ต้องแปลก่อนหรือรับรองก่อน",
    ],
    cluster: "process",
  },

  // ── เอกสาร / เนื้อหาคำแปล ────────────────────────────────────────
  {
    slug: "sworn-คำแปลต้องมีอะไรครบ",
    q: "คำแปลสาบานตนต้องมีองค์ประกอบอะไรครบ?",
    aShort:
      "ต้องมีคำแปลครบทุกข้อความรวมถึงตราประทับและลายมือชื่อ คำรับรองของผู้แปล ลายมือชื่อและตราของนักแปล และการอ้างอิงเลขที่เอกสารต้นฉบับ",
    aLong: `จุดที่ถูกตีกลับบ่อยคือ ตกคำแปลของตราประทับ ตกข้อความท้ายเอกสาร ไม่ระบุว่าแปลจากต้นฉบับหรือสำเนา และไม่แนบสำเนาต้นฉบับไว้กับคำแปล ควรตรวจสี่จุดนี้ก่อนส่งไปยื่นทุกครั้ง ${NOTE} ${CTA}`,
    synonyms: [
      "องค์ประกอบคำแปลรับรอง",
      "sworn translation requirements",
      "คำรับรองผู้แปล",
    ],
    cluster: "documents",
  },
  {
    slug: "sworn-การสะกดชื่อในคำแปล",
    q: "การสะกดชื่อในคำแปลควรยึดอะไรเป็นหลัก?",
    aShort:
      "ยึดหนังสือเดินทางเป็นหลัก และแจ้งการสะกดที่ต้องการให้ผู้แปลตั้งแต่ก่อนเริ่มงาน เพื่อให้ตรงกับเอกสารชุดอื่นในคำร้องเดียวกัน",
    aLong: `ความไม่ตรงกันของการถอดอักษรเป็นสาเหตุการตีกลับที่พบมากที่สุดในงานเอกสารข้ามประเทศ ถ้าเอกสารชุดหนึ่งมีหลายฉบับ ต้องสะกดชื่อ สถานที่เกิด และชื่อหน่วยงานให้เหมือนกันทุกฉบับ กรณีเคยเปลี่ยนชื่อสกุล ควรเตรียมเอกสารเชื่อมโยงพร้อมคำแปลไปด้วย ${NOTE} ${CTA}`,
    synonyms: [
      "สะกดชื่อในคำแปล",
      "name spelling certified translation",
      "ชื่อไม่ตรงพาสปอร์ต คำแปล",
    ],
    cluster: "documents",
  },
  {
    slug: "sworn-เอกสารหลายภาษาในฉบับเดียว",
    q: "เอกสารที่มีหลายภาษาในฉบับเดียวต้องแปลทั้งหมดไหม?",
    aShort:
      "ต้องแปลทุกข้อความที่ปรากฏ รวมถึงส่วนที่เป็นภาษาอื่น เว้นแต่สถานทูตปลายทางระบุชัดว่าไม่ต้องแปลส่วนที่เป็นภาษาของตนเอง",
    aLong: `หลักของคำแปลรับรองคือความครบถ้วนเทียบต้นฉบับ ผู้แปลจึงต้องระบุกำกับว่าส่วนใดเป็นภาษาใด และไม่ละส่วนที่อ่านยากหรือมีลายมือ กรณีมีข้อความที่อ่านไม่ออก ให้ระบุหมายเหตุไว้ตามความจริง ไม่ใช่เว้นว่าง ${NOTE} ${CTA}`,
    synonyms: [
      "เอกสารสองภาษา แปลไหม",
      "bilingual document sworn translation",
      "แปลทุกข้อความ ต้นฉบับ",
    ],
    cluster: "language",
  },

  // ── ปัญหา / เวลา ─────────────────────────────────────────────────
  {
    slug: "sworn-สาเหตุถูกตีกลับ",
    q: "คำแปลสาบานตนถูกตีกลับเพราะอะไรบ่อยที่สุด?",
    aShort:
      "ห้าสาเหตุหลักคือ ใช้นักแปลนอกบัญชี คำแปลไม่ครบถ้วน สะกดชื่อไม่ตรงหนังสือเดินทาง ทำชั้นรับรองสลับลำดับ และเอกสารต้นทางเกินอายุที่ปลายทางกำหนด",
    aLong: `ทั้งห้าข้อป้องกันได้ก่อนยื่น ให้ตรวจว่านักแปลอยู่ในบัญชีที่ปลายทางกำหนด ตรวจความครบถ้วนของคำแปลทีละหน้า ตรวจการสะกดเทียบหนังสือเดินทาง ยืนยันลำดับชั้นรับรองจากเช็กลิสต์ของปลายทาง และตรวจวันที่ออกเอกสารต้นทาง ${NOTE} ${CTA}`,
    synonyms: [
      "คำแปลถูกปฏิเสธ สถานทูต",
      "why sworn translation rejected",
      "ข้อควรระวังแปลสาบานตน",
    ],
    cluster: "definition",
  },
  {
    slug: "sworn-ระยะเวลาขึ้นกับอะไร",
    q: "ระยะเวลางานแปลสาบานตนขึ้นกับอะไร?",
    aShort:
      "ขึ้นกับคู่ภาษา จำนวนหน้า คิวของนักแปลในบัญชี และจำนวนชั้นรับรองที่ปลายทางกำหนด จึงควรวางแผนเป็นช่วงวันทำการ",
    aLong: `ภาษาที่มีนักแปลในบัญชีจำนวนน้อยจะมีคิวยาวกว่าโดยธรรมชาติ และงานที่ต้องผ่านทั้งนิติกรณ์และตราสถานทูตจะใช้เวลามากกว่างานที่จบที่ชั้นคำแปล วิธีวางแผนที่ปลอดภัยคือทำงานย้อนกลับจากวันนัดยื่นจริง และเผื่อเวลาสำรองสำหรับกรณีต้องแก้ไข ${NOTE} ${CTA}`,
    synonyms: [
      "แปลสาบานตนใช้เวลา",
      "sworn translation turnaround",
      "คิวนักแปลสถานทูต",
    ],
    cluster: "speed",
  },
  {
    slug: "sworn-ต้องทำนิติกรณ์ต่อไหม",
    q: "แปลสาบานตนแล้วต้องทำนิติกรณ์กรมการกงสุลต่อไหม?",
    aShort:
      "ไม่เสมอไป บางปลายทางถือว่าคำแปลของนักแปลในบัญชีเพียงพอ แต่บางปลายทางยังขอชั้นนิติกรณ์และตราสถานทูตเพิ่ม จึงต้องอ่านเช็กลิสต์ก่อน",
    aLong: `${FEE} การทำชั้นที่ปลายทางไม่ได้ขอทำให้เสียค่าธรรมเนียมและเวลาโดยไม่จำเป็น ในทางกลับกัน การข้ามชั้นที่ปลายทางขอทำให้ต้องเริ่มใหม่ ทางที่ถูกคือยืนยันจำนวนชั้นกับผู้รับเอกสารก่อนเริ่มงาน ${NOTE} ${CTA}`,
    synonyms: [
      "แปลสาบานตน ต้องผ่านกงสุลไหม",
      "sworn translation need mfa legalisation",
      "กี่ชั้น รับรองเอกสาร ยุโรป",
    ],
    cluster: "apostille",
  },
  {
    slug: "sworn-apostille-มีผลกับงานแปลไหม",
    q: "เมื่อไทยเริ่มออก Apostille แล้ว งานแปลสาบานตนจะเปลี่ยนไหม?",
    aShort:
      "ชั้นตราสถานทูตปลายทางมีแนวโน้มลดลงสำหรับรัฐภาคี แต่ข้อกำหนดเรื่องผู้แปลยังเป็นอำนาจของปลายทาง จึงยังต้องอ่านเช็กลิสต์ของผู้รับเอกสารเสมอ",
    aLong: `อนุสัญญาจัดการเรื่องการรับรองความถูกต้องของเอกสารราชการ ไม่ได้กำหนดว่าใครต้องเป็นผู้แปล ดังนั้นสถานทูตหรือหน่วยงานปลายทางยังสามารถกำหนดให้ใช้นักแปลในบัญชีของตนต่อไปได้ ระหว่างช่วงเปลี่ยนผ่านควรยืนยันรูปแบบที่ปลายทางรับในวันยื่นจริง ${NOTE} ${CTA}`,
    synonyms: [
      "apostille กับแปลสาบานตน",
      "apostille effect on sworn translation",
      "ไทยออก apostille แล้วต้องแปลอย่างไร",
    ],
    cluster: "apostille",
  },
  {
    slug: "sworn-ต่างจังหวัดใช้บริการได้ไหม",
    q: "อยู่ต่างจังหวัดหรือต่างประเทศ ใช้บริการแปลสาบานตนได้ไหม?",
    aShort:
      "ได้ โดยส่งไฟล์สแกนคมชัดทุกหน้าเพื่อเริ่มงานแปล และส่งต้นฉบับตามมาเมื่อชั้นที่ต้องใช้ต้นฉบับกำหนด",
    aLong: `การสแกนควรเป็นสีและครบทั้งหน้า รวมถึงขอบเอกสารและตราประทับ เพื่อให้ผู้แปลอ่านข้อความได้ทุกส่วน กรณีต้องส่งต้นฉบับ ควรใช้บริการขนส่งที่ติดตามสถานะได้และเก็บสำเนาไว้ทุกหน้าก่อนส่ง ${NOTE} ${CTA}`,
    synonyms: [
      "ส่งเอกสารแปลสาบานตนออนไลน์",
      "sworn translation remote thailand",
      "อยู่ต่างประเทศ แปลเอกสารไทย",
    ],
    cluster: "process",
  },
  {
    slug: "sworn-เอกสารบริษัทแปลสาบานตนได้ไหม",
    q: "เอกสารบริษัทแปลสาบานตนได้ไหม และต้องเตรียมอะไรเพิ่ม?",
    aShort:
      "ได้ โดยควรเตรียมหนังสือรับรองบริษัทฉบับใหม่ และตรวจว่าผู้ลงนามในเอกสารตรงกับผู้มีอำนาจตามหนังสือรับรองนั้น",
    aLong: `งานเอกสารนิติบุคคลติดขัดบ่อยที่ความไม่สอดคล้องระหว่างผู้ลงนามกับหนังสือรับรองล่าสุด และการใช้ชื่อบริษัทภาษาอังกฤษหลายรูปแบบในเอกสารชุดเดียวกัน ควรกำหนดชื่อภาษาอังกฤษให้ตรงกันทุกฉบับตั้งแต่ต้น ${NOTE} ${CTA}`,
    synonyms: [
      "แปลเอกสารบริษัท สถานทูต",
      "company document sworn translation",
      "หนังสือรับรองบริษัท แปลสาบานตน",
    ],
    cluster: "documents",
  },
];
